/**
 * kvkkDijitalOnayService.js — Kişi Detay › Kontroller › KVKK kartının iki satırı: "Dijital Onay" (SMS ile gönder, kişi onaylar,
 * üretilen belge, 1 yıl geçerlilik) ve "Islak İmzalı Belge" (Yok | Alındı + yükleme, 1 yıl geçerlilik).
 * Backlog #376 (backend #375).
 *
 * ⚠️ BACKEND #375 YAPILMADI → `USE_MOCK_DATA = true`. Ekran, #375'in WORK listesindeki sözleşmeye göre kuruldu; #375 bitince
 * bayrağı kapatın. Mock yalnızca bu dosyada: bellekte kişi başına bir kayıt tutar (sayfa yenilenince sıfırlanır) ve gerçek okumanın
 * üstüne biner — gerçek `kvkkDijitalOnay`, `documents.kvkk` ve `personKvkkStatusId` her zaman temel alınır.
 *
 * ── Varsayılan sözleşme (#375'e yazıldı; değişirse yalnızca burası ve `kvkkDurumu` değişir) ──────────────────────────────────
 *  OKUMA — kişi satırındaki (`GET /personnel/{id}/detay` → `kisi`) `kvkkDijitalOnay` mevcut alanlara ek olarak:
 *    onayTarihi            "yyyy-MM-dd HH:mm:ss"  son dijital onayın zamanı (yoksa null)
 *    sonGecerlilikTarihi   "yyyy-MM-dd"           onayTarihi + 1 yıl; `tamam` bu tarihe kadar true
 *    suresiDoldu           boolean                bir kez onaylanmış ama sonGecerlilikTarihi geçmiş
 *    belgeVar              boolean                üretilen KVKK belgesi indirilebilir
 *    gonderim              { durum: 'BEKLIYOR', gonderimTarihi, sonKullanma } | null   son gönderilen ve henüz onaylanmamış bağlantı
 *  Satırda ayrıca `personKvkkStatusSonGecerlilik` ("yyyy-MM-dd"): "Alındı" (personKvkkStatusId 3) işaretinin tarihi + 1 yıl.
 *  Yüklenen ıslak imzalı dosyanın süresi zaten `documents.kvkk.expiryDate` (yükleme + 1 yıl; ESKİ dosyalar 2030-01-01 kalır).
 *  YAZMA — `POST /personnel/{id}/kvkk-gonder` (SAKA personeli; telefonsuz kişi 400, birleştirilmiş kart 409). Yanıt zarfının `data`sı
 *  güncel `kvkkDijitalOnay`. SMS sağlayıcısı şimdilik Noop: gerçek SMS gitmez, onayı kişi bağlantıdan verir (herkese açık sayfa
 *  ayrı bir görev). Belgenin kendisi `GET /personnel/{id}/kvkk-belgesi` (yalnızca personel, no-store; TC taşır) → blob.
 *  "Alındı" = mevcut `PATCH /personnel/{id}/kvkk-status` (3 / 0); yükleme = mevcut `POST /personnel/kartduzenle/{id}/kvkk`.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { currentLocaleTag } from "../i18n";

export const USE_MOCK_DATA = true;

const BASE = `${JAVA_BACKEND_API_URL}/personnel`;

// ─── Tarih yardımcıları (yerel takvim günü; toISOString UTC günü verir) ──────────────────────────────────────────────────────

const iki = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${iki(d.getMonth() + 1)}-${iki(d.getDate())}`;
const ymdHms = (d) => `${ymd(d)} ${iki(d.getHours())}:${iki(d.getMinutes())}:${iki(d.getSeconds())}`;

/** "yyyy-MM-dd[ HH:mm:ss]" → Date (yerel). Boş / bozuk → null. */
export function tarihCoz(v) {
  if (!v) return null;
  const d = new Date(String(v).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
}

function birYilSonra(d) {
  const s = new Date(d.getTime());
  s.setFullYear(s.getFullYear() + 1);
  return ymd(s);
}

// ─── Mock deposu (yalnızca USE_MOCK_DATA) ───────────────────────────────────────────────────────────────────────────────────

/** personId → { bekleyen, onay, alindiTarihi, yuklemeTarihi } */
const MOCK = new Map();
const mockKayit = (personId) => {
  if (!MOCK.has(personId)) MOCK.set(personId, {});
  return MOCK.get(personId);
};

// Görsel kontrol için konsoldan: `kvkkMock.sureDoldur(78303)` onayı geçmişe çeker, `kvkkMock.sifirla(78303)` kaydı siler.
if (USE_MOCK_DATA && typeof window !== 'undefined') {
  window.kvkkMock = {
    sureDoldur(personId) {
      const k = mockKayit(Number(personId));
      const gecmis = new Date();
      gecmis.setFullYear(gecmis.getFullYear() - 1);
      gecmis.setDate(gecmis.getDate() - 3);
      k.onay = { tarih: ymdHms(gecmis), sonGecerlilik: birYilSonra(gecmis), belgeVar: true };
    },
    sifirla(personId) { MOCK.delete(Number(personId)); },
  };
}

// ─── Görünüm modeli ─────────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * KVKK kartının iki satırını çizmeye yetecek tek nesne. Hesap burada; bileşen yalnızca çizer.
 *
 * @param {number} personId
 * @param {object} person    kişi satırı (`detay.kisi`)
 * @param {object} kvkkBelge `person.documents.kvkk` (ıslak imzalı dosya)
 * @param {Date}   [simdi]
 * @returns {{
 *   dijital: { durum: 'YOK'|'GONDERILDI'|'ONAYLANDI'|'SURESI_DOLDU'|'EKSIK', onay: object|null, bekleyen: object|null,
 *              eksik: object|null, gonderEtiketi: string, telefonYok: boolean },
 *   islak:   { alindi: boolean, belgeVar: boolean, sonGecerlilik: string|null, suresiDoldu: boolean },
 * }}
 */
export function kvkkDurumu(personId, person, kvkkBelge, simdi = new Date()) {
  const bugun = ymd(simdi);
  const d = person?.kvkkDijitalOnay ?? null;
  const m = USE_MOCK_DATA ? MOCK.get(Number(personId)) : null;

  // ── Dijital onay ──
  let onay = null;
  if (m?.onay) {
    onay = m.onay;
  } else if (d && (d.onayTarihi || d.tamam)) {
    onay = {
      tarih: d.onayTarihi ?? d.acikRizaTarihi ?? d.aydinlatmaTarihi ?? null,
      sonGecerlilik: d.sonGecerlilikTarihi ?? null,
      belgeVar: !!d.belgeVar,
    };
  }
  const onaySuresiDoldu = !!onay && (onay.sonGecerlilik ? onay.sonGecerlilik < bugun : d?.suresiDoldu === true);

  let bekleyen = m?.bekleyen ?? (d?.gonderim?.durum === 'BEKLIYOR' ? d.gonderim : null);
  // Süresi dolmuş bağlantı artık onaylanamaz: bekleyen sayılmaz, personel yeniden gönderir.
  if (bekleyen?.sonKullanma && tarihCoz(bekleyen.sonKullanma) < simdi) bekleyen = null;

  // Eski kısmi durum: yalnız bir metin onaylanmış (yeni akış ikisini birden alır).
  const eksik = !onay && d && (d.aydinlatmaTarihi || d.acikRizaTarihi) ? d : null;

  const durum = onay
    ? (onaySuresiDoldu ? 'SURESI_DOLDU' : 'ONAYLANDI')
    : bekleyen ? 'GONDERILDI' : eksik ? 'EKSIK' : 'YOK';

  // ── Islak imzalı belge ──
  const alindi = person?.personKvkkStatusId === 3;
  const belgeVar = kvkkBelge?.fileId != null;
  let sonGecerlilik = null;
  if (belgeVar) {
    const yukleme = USE_MOCK_DATA ? tarihCoz(m?.yuklemeTarihi) : null;
    sonGecerlilik = yukleme ? birYilSonra(yukleme) : (kvkkBelge.expiryDate ? String(kvkkBelge.expiryDate).slice(0, 10) : null);
  } else if (alindi) {
    const alindiTarihi = USE_MOCK_DATA ? tarihCoz(m?.alindiTarihi) : null;
    sonGecerlilik = alindiTarihi ? birYilSonra(alindiTarihi) : (person?.personKvkkStatusSonGecerlilik ?? null);
  }

  return {
    dijital: {
      durum, onay: onay && { ...onay, suresiDoldu: onaySuresiDoldu }, bekleyen, eksik,
      gonderEtiketi: onay || bekleyen ? 'Yeniden Gönder' : 'KVKK Gönder',
      telefonYok: !String(person?.phone ?? '').replace(/\D/g, ''),
    },
    islak: { alindi, belgeVar, sonGecerlilik, suresiDoldu: !!sonGecerlilik && sonGecerlilik < bugun },
  };
}

/** Bayrak açıkken kart "Onayı simüle et" düğmesini gösterir (gerçek onay kişinin bağlantısından gelir). */
export const kvkkMockModu = () => USE_MOCK_DATA;

// ─── Yazma ──────────────────────────────────────────────────────────────────────────────────────────────────────────────────

function hataCevir(error) {
  const govde = error.response?.data;
  const message = !error.response
    ? 'Sunucu yanıt vermedi.'
    : typeof govde === 'string' && govde
      ? govde
      : govde?.message ?? error.message ?? 'İşlem başarısız oldu.';
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  wrapped.data = govde?.data ?? null;
  return wrapped;
}

/**
 * Kişiye KVKK bağlantısını gönderir. Mock: bağlantı 7 gün geçerli sayılır. Eski bir onay varsa İPTAL ETMEZ — yeni onay gelene
 * kadar yürürlükte kalır (#375 kararı).
 * POST /api/v1/personnel/{personId}/kvkk-gonder
 */
export async function kvkkGonder(personId) {
  if (USE_MOCK_DATA) {
    const simdi = new Date();
    const son = new Date(simdi.getTime() + 7 * 24 * 3600 * 1000);
    mockKayit(Number(personId)).bekleyen = { durum: 'BEKLIYOR', gonderimTarihi: ymdHms(simdi), sonKullanma: ymdHms(son) };
    return { message: 'KVKK bağlantısı gönderildi.', result: true };
  }
  let response;
  try {
    response = await axios.post(`${BASE}/${personId}/kvkk-gonder`, {}, { headers: getAuthHeader() });
  } catch (error) {
    throw hataCevir(error);
  }
  const govde = response.data;
  if (govde && typeof govde === 'object' && govde.result === false) {
    const hata = new Error(govde.message || 'KVKK bağlantısı gönderilemedi.');
    hata.status = response.status;
    throw hata;
  }
  return govde;
}

/** YALNIZCA MOCK: kişinin bağlantıdan onay vermesini taklit eder (onay + 1 yıl, belge üretilmiş). */
export async function kvkkOnayiSimuleEt(personId) {
  if (!USE_MOCK_DATA) throw new Error('Onay yalnızca kişinin bağlantısından gelir.');
  const simdi = new Date();
  const k = mockKayit(Number(personId));
  k.onay = { tarih: ymdHms(simdi), sonGecerlilik: birYilSonra(simdi), belgeVar: true };
  k.bekleyen = null;
  return { message: 'KVKK onayı alındı (örnek).', result: true };
}

/** Mock: "Alındı" işareti ve yükleme tarihi — gerçek yazma mevcut uçlarla yapılır, burası yalnızca 1 yıllık süreyi hatırlar. */
export function kvkkMockAlindi(personId, alindi) {
  if (!USE_MOCK_DATA) return;
  mockKayit(Number(personId)).alindiTarihi = alindi ? ymdHms(new Date()) : null;
}
export function kvkkMockYuklendi(personId) {
  if (!USE_MOCK_DATA) return;
  mockKayit(Number(personId)).yuklemeTarihi = ymdHms(new Date());
}

// ─── Üretilen belge ─────────────────────────────────────────────────────────────────────────────────────────────────────────

const kacis = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const gunYaz = (v) => { const d = tarihCoz(v); return d ? d.toLocaleDateString(currentLocaleTag()) : '—'; };

function mockBelgeHtml(person, onay) {
  const ad = [person?.name, person?.surname].filter(Boolean).join(' ');
  const satir = (k, v) => `<tr><th>${k}</th><td>${kacis(v)}</td></tr>`;
  return `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>KVKK Belgesi — ${kacis(ad)}</title>
<style>body{font:15px/1.5 system-ui,sans-serif;max-width:680px;margin:40px auto;padding:0 20px;color:#222}
h1{font-size:20px}th{text-align:left;padding:6px 16px 6px 0;color:#666;font-weight:600}td{padding:6px 0}
.not{margin-top:28px;padding:10px 14px;border:1px dashed #c00;color:#c00;font-size:13px}</style></head><body>
<h1>KVKK Aydınlatma ve Açık Rıza Onayı</h1>
<table>${satir('Ad Soyad', ad)}${satir('T.C. Kimlik No', person?.tcNo ?? '—')}${satir('Telefon', person?.phone ?? '—')}
${satir('Onay kanalı', 'SMS bağlantısı')}${satir('Onay tarihi', gunYaz(onay?.tarih))}${satir('Son geçerlilik', gunYaz(onay?.sonGecerlilik))}</table>
<p class="not">ÖRNEK BELGE — backend #375 hazır olana kadar tarayıcıda üretilir; hukuki geçerliliği yoktur.</p></body></html>`;
}

/**
 * Üretilen KVKK belgesinin blob adresi (çağıran `window.open` sonrası `URL.revokeObjectURL` eder). Belge TC taşır: yalnızca personel,
 * önbelleğe alınmaz.
 * GET /api/v1/personnel/{personId}/kvkk-belgesi
 */
export async function kvkkBelgesiUrl(personId, person) {
  if (USE_MOCK_DATA) {
    const { onay } = kvkkDurumu(personId, person, null).dijital;
    return URL.createObjectURL(new Blob([mockBelgeHtml(person, onay)], { type: 'text/html;charset=utf-8' }));
  }
  try {
    const response = await axios.get(`${BASE}/${personId}/kvkk-belgesi`, { headers: getAuthHeader(), responseType: 'blob' });
    return URL.createObjectURL(response.data);
  } catch (error) {
    // responseType:'blob' hata gövdesini de Blob yapar — mesajı okumak için çöz.
    let message = error.message;
    const blob = error.response?.data;
    if (blob instanceof Blob) {
      try { message = JSON.parse(await blob.text()).message || message; } catch { /* JSON değil */ }
    }
    const wrapped = new Error(message);
    wrapped.status = error.response?.status ?? null;
    throw wrapped;
  }
}
